// src/hooks/useFirebase.js

import {
  ref,
  set,
  push,
  onValue,
  remove,
  update
} from 'firebase/database';

import {
  useEffect,
  useState
} from 'react';

import { db } from '../firebase';

// ===========================
// イベント
// ===========================

export const useEvents = () => {
  const [events, setEvents] =
    useState({});

  useEffect(() => {
    const eventsRef = ref(
      db,
      'events'
    );

    const unsubscribe = onValue(
      eventsRef,
      (snapshot) => {
        setEvents(
          snapshot.val() || {}
        );
      }
    );

    return () => unsubscribe();
  }, []);

  const createEvent = (
    eventName,
    matchingMode = 'cast_select',
    ownerUid = null,
    eventType = 'matching',
    capacity = null,
    instanceOptions = null
  ) => {
    const newRef = push(ref(db, 'events'));
    const data = {
      event_name: eventName,
      status: 'waiting',
      matching_mode: matchingMode,
      event_type: eventType,
      owner_id: ownerUid,
      created_at: Date.now(),
      casts: {},
      guests: {}
    };
    if (capacity !== null && capacity !== '') data.capacity = Number(capacity);
    if (instanceOptions) {
      data.instance_mode = instanceOptions.mode;
      if (instanceOptions.mode === 'count' && instanceOptions.count !== '') {
        data.instance_count = Number(instanceOptions.count);
      } else if (instanceOptions.mode === 'capacity' && instanceOptions.capacity !== '') {
        data.instance_capacity = Number(instanceOptions.capacity);
      }
    }
    set(newRef, data);
  };

  const updateCapacity = (eventId, capacity) => {
    update(ref(db, `events/${eventId}`), {
      capacity: capacity === '' ? null : Number(capacity),
    });
  };

  const deleteEvent = (
    eventId
  ) => {
    remove(
      ref(db, `events/${eventId}`)
    );
  };

  const updateEventStatus = (
    eventId,
    status
  ) => {
    update(
      ref(db, `events/${eventId}`),
      {
        status
      }
    );
  };

  const updateMatchingMode = (
    eventId,
    matchingMode
  ) => {
    update(
      ref(db, `events/${eventId}`),
      {
        matching_mode: matchingMode
      }
    );
  };

  const clearAllDraftPicks = async (eventId) => {
    const eventSnapshot = await new Promise((resolve) => {
      onValue(ref(db, `events/${eventId}`), resolve, { onlyOnce: true });
    });
    const eventData = eventSnapshot.val();
    if (!eventData) {
      return;
    }

    const updates = {};
    if (eventData.casts) {
      Object.keys(eventData.casts).forEach((castId) => {
        updates[`events/${eventId}/casts/${castId}/draft_picks`] = null;
      });
    }
    if (eventData.guests) {
      Object.keys(eventData.guests).forEach((guestId) => {
        updates[`events/${eventId}/guests/${guestId}/draft_picks`] = null;
      });
    }
    
    if (Object.keys(updates).length > 0) {
      await update(ref(db), updates);
    }
  };

  const updateInstanceSettings = (eventId, { instanceCount, instanceCapacity, instanceMode }) => {
    const updates = { instance_mode: instanceMode };
    if (instanceMode === 'count') {
      updates.instance_count    = instanceCount !== '' ? Number(instanceCount) : null;
      updates.instance_capacity = null;
    } else {
      updates.instance_capacity = instanceCapacity !== '' ? Number(instanceCapacity) : null;
      updates.instance_count    = null;
    }
    update(ref(db, `events/${eventId}`), updates);
  };

  const generateNominationToken = async (eventId) => {
    const token = crypto.randomUUID();
    await Promise.all([
      set(ref(db, `events/${eventId}/nomination_token`), token),
      set(ref(db, `nominationTokens/${token}`), { event_id: eventId }),
    ]);
    return token;
  };

  const deleteNominationToken = async (eventId, token) => {
    if (!token) return;
    await Promise.all([
      remove(ref(db, `events/${eventId}/nomination_token`)),
      remove(ref(db, `nominationTokens/${token}`)),
    ]);
  };

  return {
    events,
    createEvent,
    deleteEvent,
    updateEventStatus,
    updateMatchingMode,
    updateCapacity,
    updateInstanceSettings,
    clearAllDraftPicks,
    generateNominationToken,
    deleteNominationToken,
  };
};

// ===========================
// キャスト
// ===========================

export const useCasts = (
  eventId
) => {
  const [casts, setCasts] =
    useState({});

  useEffect(() => {
    setCasts({}); // イベント切替時にスタレートデータを即クリア
    if (!eventId) return;

    const castsRef = ref(
      db,
      `events/${eventId}/casts`
    );

    const unsubscribe = onValue(
      castsRef,
      (snapshot) => {
        const data = snapshot.val() || {};

        // name → vrchat_name 移行（古いキーを削除）
        const migrations = {};
        Object.entries(data).forEach(([castId, cast]) => {
          if (!cast.vrchat_name && cast.name) {
            data[castId] = { ...cast, vrchat_name: cast.name };
            delete data[castId].name;
            migrations[`events/${eventId}/casts/${castId}/vrchat_name`] = cast.name;
            migrations[`events/${eventId}/casts/${castId}/name`] = null;
          } else if (cast.vrchat_name && cast.name) {
            delete data[castId].name;
            migrations[`events/${eventId}/casts/${castId}/name`] = null;
          }
        });
        if (Object.keys(migrations).length > 0) update(ref(db), migrations);

        setCasts(data);
      }
    );

    return () => unsubscribe();
  }, [eventId]);

  /**
   * @param {{ vrchat_name: string }} castData
   */
  const addCast = (castData) => {
    const newRef = push(ref(db, `events/${eventId}/casts`));
    set(newRef, {
      ...castData,
      status: 'absent',
      tags: [],
      priority: 0,
      draft_picks: [],
    });
  };

  const updateCast = (castId, updates) => {
    update(ref(db, `events/${eventId}/casts/${castId}`), updates);
  };

  const removeCast = (
    castId
  ) => {
    remove(
      ref(
        db,
        `events/${eventId}/casts/${castId}`
      )
    );
  };

  const toggleCastStatus = (
    castId,
    currentStatus
  ) => {
    update(
      ref(
        db,
        `events/${eventId}/casts/${castId}`
      ),
      {
        status:
          currentStatus ===
          'working'
            ? 'absent'
            : 'working'
      }
    );
  };

  const updateCastTags = (
    castId,
    tags
  ) => {
    update(
      ref(
        db,
        `events/${eventId}/casts/${castId}`
      ),
      {
        tags
      }
    );
  };

  const setAllCastsAbsent =
    async () => {
      const updates = {};

      Object.keys(casts).forEach(
        (castId) => {
          updates[
            `events/${eventId}/casts/${castId}/status`
          ] = 'absent';
        }
      );

      await update(ref(db), updates);
    };

  const updateCastPriority = (castId, priority) => {
    update(ref(db, `events/${eventId}/casts/${castId}`), { priority });
  };

  const updateCastDraftPicks = (castId, draftPicks) => {
    update(ref(db, `events/${eventId}/casts/${castId}`), { draft_picks: draftPicks });
  };

  return {
    casts,
    addCast,
    updateCast,
    removeCast,
    toggleCastStatus,
    updateCastTags,
    setAllCastsAbsent,
    updateCastPriority,
    updateCastDraftPicks,
  };
};

// ===========================
// ゲスト
// ===========================

export const useGuests = (
  eventId
) => {
  const [guests, setGuests] =
    useState({});

  useEffect(() => {
    setGuests({}); // イベント切替時にスタレートデータを即クリア
    if (!eventId) return;

    const guestsRef = ref(
      db,
      `events/${eventId}/guests`
    );

    const unsubscribe = onValue(
      guestsRef,
      (snapshot) => {
        setGuests(snapshot.val() || {});
      }
    );

    return () => unsubscribe();
  }, [eventId]);

  const addGuest = (guest) => {
    const newRef = push(
      ref(
        db,
        `events/${eventId}/guests`
      )
    );

    set(newRef, {
      ...guest,
      status: 'waiting',
      match_result: null,
      priority: 0,
      draft_picks: [],
      created_at: Date.now()
    });
  };

  const removeGuest = (
    guestId
  ) => {
    remove(
      ref(
        db,
        `events/${eventId}/guests/${guestId}`
      )
    );
  };

  const clearGuests = () => {
    remove(
      ref(
        db,
        `events/${eventId}/guests`
      )
    );
  };

  const updateGuest = (guestId, updates) => {
    update(ref(db, `events/${eventId}/guests/${guestId}`), updates);
  };

  const updateGuestPriority = (guestId, priority) => {
    update(ref(db, `events/${eventId}/guests/${guestId}`), { priority });
  };

  const updateGuestDraftPicks = (guestId, draftPicks) => {
    update(ref(db, `events/${eventId}/guests/${guestId}`), { draft_picks: draftPicks });
  };

  return {
    guests,
    addGuest,
    removeGuest,
    clearGuests,
    updateGuest,
    updateGuestPriority,
    updateGuestDraftPicks
  };
};

// ===========================
// マッチング結果
// ===========================

export const useMatchingResults = (
  eventId
) => {
  const saveResults = (
    results
  ) => {
    update(
      ref(db, `events/${eventId}`),
      {
        results,
        status: 'matched'
      }
    );
  };

  const clearResults = () => {
    update(
      ref(db, `events/${eventId}`),
      {
        results: null,
      }
    );
  };

  return {
    saveResults,
    clearResults
  };
};